import { z } from 'zod';

const RefSchema = z.string().min(1).max(240);
const RouteSchema = z.string().min(1).max(300);

export const SdkPwaRuntimeSurfaceSchema = z.literal('pwa');
export type SdkPwaRuntimeSurface = z.infer<typeof SdkPwaRuntimeSurfaceSchema>;

export const SdkPwaRuntimeEndpointRefSchema = z.enum([
  'boox.pwa.networkContext.current',
  'boox.pwa.networkContext.switch',
  'boox.pwa.dynamicNavigation.click',
  'boox.pwa.customerPortal.supportSummary',
  'boox.pwa.customerPortal.ordersSummary',
  'boox.pwa.customerPortal.supportCase.create',
  'boox.pwa.customerPortal.supportCase.reply',
  'boox.pwa.workSchedule.daily',
  'boox.pwa.wms.taskQueue',
]);
export type SdkPwaRuntimeEndpointRef = z.infer<typeof SdkPwaRuntimeEndpointRefSchema>;

export const SdkPwaNetworkContextSwitchRequestSchema = z.object({
  pwaMode: z.literal('network_home').optional(),
  relationshipId: RefSchema.optional(),
}).superRefine((request, ctx) => {
  if (!request.pwaMode && !request.relationshipId) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['relationshipId'],
      message: 'network context switch requires either pwaMode or relationshipId.',
    });
  }
  if (request.pwaMode && request.relationshipId) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['relationshipId'],
      message: 'network context switch must not mix network_home and relationshipId.',
    });
  }
});
export type SdkPwaNetworkContextSwitchRequest = z.infer<typeof SdkPwaNetworkContextSwitchRequestSchema>;

export const SdkPwaDynamicNavigationClickSchema = z.object({
  surface: SdkPwaRuntimeSurfaceSchema.default('pwa'),
  module: RefSchema,
  route: RouteSchema,
  click: z.object({
    id: RefSchema,
    route: RouteSchema,
    label: z.string().min(1).max(240),
    activeRoute: RouteSchema,
  }),
});
export type SdkPwaDynamicNavigationClick = z.infer<typeof SdkPwaDynamicNavigationClickSchema>;

export const SdkPwaCustomerPortalSummaryRequestSchema = z.object({
  limit: z.number().int().min(1).max(50).default(5),
});
export type SdkPwaCustomerPortalSummaryRequest = z.infer<typeof SdkPwaCustomerPortalSummaryRequestSchema>;

export interface SdkPwaCustomerPortalSupportCaseSummary {
  id: string;
  caseNumber: string | null;
  caseType: string | null;
  priority: string | null;
  status: string | null;
  subject: string | null;
  sourceChannel: string | null;
  sourceRef: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  resolutionDueAt: string | null;
}

export interface SdkPwaCustomerPortalSupportRelationshipSummary {
  relationshipId: string;
  tenantId: string;
  tenantName: string;
  tenantCustomerId: string;
  partyRef?: string;
  contextKind?: string;
  pwaMode?: string;
  readOnly?: boolean;
  support: {
    open: number;
    urgent: number;
    overdue: number;
    cases: SdkPwaCustomerPortalSupportCaseSummary[];
  };
}

export interface SdkPwaCustomerPortalSupportSummary {
  contextId: string;
  userId: string;
  relationshipCount: number;
  totals: {
    open: number;
    urgent: number;
    overdue: number;
  };
  relationships: SdkPwaCustomerPortalSupportRelationshipSummary[];
}

export interface SdkPwaCustomerPortalOrderJobSummary {
  id: string;
  jobNumber: string | null;
  jobType: string | null;
  status: string | null;
  customerId: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  publicData: Record<string, unknown>;
}

export interface SdkPwaCustomerPortalOrderRelationshipSummary {
  relationshipId: string;
  tenantId: string;
  tenantName: string;
  tenantCustomerId: string;
  partyRef?: string;
  contextKind?: string;
  pwaMode?: string;
  readOnly?: boolean;
  orders: {
    open: number;
    active: number;
    jobs: SdkPwaCustomerPortalOrderJobSummary[];
  };
}

export interface SdkPwaCustomerPortalOrdersSummary {
  contextId: string;
  userId: string;
  relationshipCount: number;
  totals: {
    open: number;
    active: number;
  };
  relationships: SdkPwaCustomerPortalOrderRelationshipSummary[];
}

export const SdkPwaCustomerPortalCreateSupportCaseRequestSchema = z.object({
  relationshipId: RefSchema,
  requestType: z.string().min(1).max(120),
  subject: z.string().min(1).max(240),
  description: z.string().max(4000).nullable().optional(),
  contactEmail: z.string().max(320).nullable().optional(),
});
export type SdkPwaCustomerPortalCreateSupportCaseRequest = z.infer<
  typeof SdkPwaCustomerPortalCreateSupportCaseRequestSchema
>;

export const SdkPwaCustomerPortalReplySupportCaseRequestSchema = z.object({
  relationshipId: RefSchema,
  body: z.string().min(1).max(4000),
  contactEmail: z.string().max(320).nullable().optional(),
});
export type SdkPwaCustomerPortalReplySupportCaseRequest = z.infer<
  typeof SdkPwaCustomerPortalReplySupportCaseRequestSchema
>;

export const SdkPwaDailyScheduleRequestSchema = z.object({
  date: z.string().min(1).max(40),
  module: RefSchema.optional(),
  limit: z.number().int().min(1).max(500).default(100),
});
export type SdkPwaDailyScheduleRequest = z.infer<typeof SdkPwaDailyScheduleRequestSchema>;

export interface SdkPwaDailyScheduleWorkItem {
  id: string;
  module: string;
  category: string;
  title: string;
  subtitle: string;
  status: string;
  priority: 'high' | 'normal' | 'low';
  scheduledTime: string | null;
  dueDate: string | null;
  assignee: string | null;
  route: string;
  metadata: Record<string, unknown>;
}

export interface SdkPwaDailySchedule {
  date: string;
  totalItems: number;
  byModule: Record<string, { count: number; items: SdkPwaDailyScheduleWorkItem[] }>;
  timeline: SdkPwaDailyScheduleWorkItem[];
  summary: {
    pending: number;
    inProgress: number;
    completed: number;
    overdue: number;
    highPriority: number;
  };
}

export const SdkPwaWmsTaskQueueRequestSchema = z.object({
  date: z.string().min(1).max(40),
  mode: RefSchema.optional(),
});
export type SdkPwaWmsTaskQueueRequest = z.infer<typeof SdkPwaWmsTaskQueueRequestSchema>;

export interface SdkPwaWmsTaskQueueItem {
  id: string;
  refCode: string;
  refId: string;
  summary: string;
  meta: unknown;
}

export interface SdkPwaCurrentNetworkContextResponse<TContext> {
  context?: TContext | null;
}

export interface SdkPwaRuntimeClient<TContext> {
  getCurrentNetworkContext: () => Promise<SdkPwaCurrentNetworkContextResponse<TContext>>;
  switchNetworkContext: (request: SdkPwaNetworkContextSwitchRequest) => Promise<TContext>;
  recordDynamicNavigationClick: (event: SdkPwaDynamicNavigationClick) => Promise<void>;
  getCustomerPortalSupportSummary: (
    request?: Partial<SdkPwaCustomerPortalSummaryRequest>,
  ) => Promise<SdkPwaCustomerPortalSupportSummary>;
  getCustomerPortalOrdersSummary: (
    request?: Partial<SdkPwaCustomerPortalSummaryRequest>,
  ) => Promise<SdkPwaCustomerPortalOrdersSummary>;
  createCustomerPortalSupportCase: (
    request: SdkPwaCustomerPortalCreateSupportCaseRequest,
  ) => Promise<void>;
  replyCustomerPortalSupportCase: (
    caseId: string,
    request: SdkPwaCustomerPortalReplySupportCaseRequest,
  ) => Promise<void>;
  getDailySchedule: (
    request: Partial<SdkPwaDailyScheduleRequest> & Pick<SdkPwaDailyScheduleRequest, 'date'>,
  ) => Promise<SdkPwaDailySchedule>;
  getWmsTaskQueue: (request: SdkPwaWmsTaskQueueRequest) => Promise<SdkPwaWmsTaskQueueItem[]>;
}
